/**
 * @ Author: Housefolios
 * @ Create Time: 2022-06-17 10:57:13
 * @ Modified by: David Helmick
 * @ Modified time: 2025-07-21 15:27:41
 * @ Description: BOR Strategy Analysis page
 */

import React, { Suspense, lazy, useState } from 'react'
import {
  strategyBlockSpacerSx,
  strategyCardBodyPadSx,
  strategyCardTitleSx,
  strategyFlexCenterOnlySx,
  strategyFlexCenterRowSx,
  strategyFlexGrowSx,
  strategyFooterNoteSx,
  strategyFullHeight100Sx,
  strategyFullWidthHiddenSx,
  strategyIconGap20Style,
  strategyIconGapStyle,
  strategyMarginBottom10Sx,
  strategyMarginBottom25Sx,
  strategyMaxHeight440Sx,
  strategyMetricGrayBgSx,
  strategyMetricRowSx,
  strategyMutedDividerSx,
  strategyMutedTextSx,
  strategyNavyBgSx,
  calcDialogPaperSx,
  strategySectionLabelSx,
  strategyTableRowLastSx,
  strategyTableRowSx,
  strategyUppercaseMetaSx,
  strategyWhiteTextSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import { useQuery } from '@apollo/client/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'

import {
  filter,
  map,
  round,
  sum,
  isEmpty,
  concat,
  last,
  includes} from '@/utils/lodash'
const Chart = lazy(() => import('@/utils/SafeApexChart'))

import {Grid, Button, Card, CardActions, CardContent, CardHeader, Dialog, Divider,  List, ListItem, ListItemButton, Tooltip, Typography, Collapse, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box } from '@mui/material'

import ContactAgent from '../../../Marketplace/ContactAgent'

import withSession from '@/housefolios-components/Session/withSession'
import { assumptionsOptions } from '@/housefolios-components/AssumptionsSettings/Accordion'
import { GET_CARDS } from '@/housefolios-components/Properties/queries'
import { DEFAULT_ANALYTICS } from '@/housefolios-components/Property/fragments'
import {
  BORBORDetailsTour,
  BORBORInputsTour,
  BORComparablePropertiesTour,
  BORStrategyTour,
  BORTargetsTour} from './tours'

import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'
import {
  ComparablePropertiesCard,
  DeactiveCard,
  StrategyCard,
  StrategyPageBase,
  TargetsCard,
  calculateCompData,
  createChartColors,
  createCurrencyFormat,
  createDonutChartOptions,
  createPercentageFormat,
  createStrategySummary,
  useStrategyPage} from '../Common'

function CenterPageBOR(props) {
  const {
    property,
    analysisInput,
    assumptionsInput,
    renderInputFields} = props

  const {
    modal1,
    openStrategyTour,
    openTargetsTour,
    openDetailsTour,
    openComparablePropertiesTour,
    toggle1,
    toggle1Close,
    toggleStrategyTour,
    toggleTargetsTour,
    toggleDetailsTour,
    toggleComparablePropertiesTour,
    closeStrategyTour,
    closeTargetsTour,
    closeDetailsTour,
    closeComparablePropertiesTour} = useStrategyPage({ strategyName: 'BOR'  })

  // Page-specific extra tour (not in useStrategyPage)
  const [openBORBORInputsTour, setOpenBORBORInputsTour] =
    useState(false)

  let results = useQuery(GET_CARDS, {
    variables: { view: 'BOR' }})
  let data = results.data || {}
  const cardsLoading = results.loading
  const cardsError = results.error
  let { cards = [] } = data

  // const { activeOrg = {} } = props.session.me || {}
  const activeOrg = property?.portfolio?.organization
  const { acquisition = {}, assumptions = {} } = property
  const { analytics = DEFAULT_ANALYTICS, analysis = {} } = acquisition

  let IRRDataFirst = {}
  let IRRData = []

  const IRRTable = (args) => {
    let {
      valueAppreciation,
      holdingPeriod,
      cashFlowTotal,
      arrInHoldingPeriodMonthly,
      expensesInHoldingPeriodMonthly,
      totalOutOfPocket,
      growthRateIncome,
      growthRateExpense} = args

    let income = arrInHoldingPeriodMonthly
    let cashFlowInHoldingPeriodMonthly =
      income - expensesInHoldingPeriodMonthly
    cashFlowTotal += cashFlowInHoldingPeriodMonthly * 12

    let month
    let year
    let monthYear

    const today = new Date()

    const IRRDate = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1,
    )

    for (let index = 1; index < holdingPeriod + 1; index++) {
      if (index === 1) {
        month = IRRDate.toLocaleString('default', { month: 'long'  })
        year = IRRDate.getFullYear().toString()
        monthYear = month + ' ' + year
        IRRDataFirst = {
          totalOutOfPocket,
          monthYear}
      } else {
        IRRDate.setMonth(IRRDate.getMonth() + 1)
        let month = IRRDate.toLocaleString('default', {
          month: 'long'})
        let year = IRRDate.getFullYear().toString()
        monthYear = month + ' ' + year
        if (index % 12 === 1) {
          IRRData.push({
            flow:
              IRRData.length === 0
                ? cashFlowTotal
                : arrInHoldingPeriodMonthly,
            monthYear,
          })
        }
      }
    }
  }

  IRRTable({
    valueAppreciation: analysis.afterRepairValue,
    holdingPeriod: assumptions.termBOR * 12,
    cashFlowTotal: assumptions.downPaymentBOR,
    arrInHoldingPeriodMonthly: sum(analytics.BOR.annualRentBOR) / 12,
    expensesInHoldingPeriodMonthly: 0,
    totalOutOfPocket: analytics.BOR.allInCostBOR,
    growthRateIncome: assumptions.growthRateIncome,
    growthRateExpense: assumptions.growthRateIncome,
  })

  const tableHeadersFirst = [
    { title: '', name: 'Date', align: 'left' },
    { title: '', name: 'Total Out of Pocket', align: 'center' },
  ]

  const tableHeaders = [
    { title: '', name: 'Date', align: 'left' },
    { title: '', name: 'Flow', align: 'center' },
  ]

  const currencyFormat = createCurrencyFormat()
  const currencyFormat2 = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 })
  const percentageFormat = createPercentageFormat()
  const pieColors = createChartColors()

  const [accordion, setAccordion] = useState(-1)

  const toggleAccordion = (index) => {
    if (index === accordion) setAccordion(-1)
    else setAccordion(index)
  }

  const BORFields = filter(
    [
      {
        type: 'dollar',
        name: 'offerPrice',
        label: 'Purchase Price',
        tooltipButtonText: 'Show Purchase Price data',
        value:
          analysisInput.offerPrice === null
            ? //? analysisInput.listPrice.toString()
              ''
            : analysisInput.offerPrice.toString(),
        helper:
          !analysisInput.offerPrice ||
          analysisInput.offerPrice === '0'
            ? '0% Discount to Price'
            : (
                (1 -
                  analysisInput.offerPrice /
                    analysisInput.listPrice) *
                100
              ).toFixed(0) + '% Discount to Price',
        xs: 12,
        lg: 6,
        definition: 'offerPrice',
        tooltipField:
          'The price that the seller is asking for this home. *Not necessarily the price that the investor will purchase the home for.',
        dataTour: 'BORDetails-step-1'},
      {
        type: 'dollar',
        name: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? 'afterRepairValueBOR'
          : 'afterRepairValue',
        label: 'After Repair Value',
        tooltipButtonText: 'Show ARV data',
        value: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? analysisInput.afterRepairValueBOR === null
            ? ''
            : analysisInput.afterRepairValueBOR.toString()
          : analysisInput.afterRepairValue === null
            ? ''
            : analysisInput.afterRepairValue.toString(),
        //helper: currencyFormat.format(analytics.BOR.equity) + ' in equity',
        xs: 12,
        lg: 6,
        definition: 'afterRepairValue',
        definitionTarget: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? 'afterRepairValueBOR'
          : 'afterRepairValue',
        tooltipField:
          'The ARV is the value of a property after you have conducted repairs and are ready to sell',
        dataTour: 'BORDetails-step-2'},
      {
        type: 'dropdown',
        name: 'closingCostBOR',
        value: assumptionsInput.closingCostBOR,
        label: 'Closing Cost',
        adornmentToggle: assumptionsInput.closingCostBORType,
        adornment: assumptionsInput.closingCostBORType,
        adornmentName: 'closingCostBORType',
        xs: 12,
        lg: 6,
        assumptions: true,
        dropdown: true,
        tooltipField:
          'Fees (attorney/title fees, etc) Paid at Closing. Expressed as a dollar amount or precentage of the purchase prices.',
        dataTour: 'BORDetails-step-3'},
      {
        type: 'dollar',
        name: `rentBOR.${props.activeUnit}`,
        label: 'Monthly Income',
        tooltipButtonText: 'Show Monthly Income data',
        value: !analysisInput.rentBOR[props.activeUnit]
          ? ''
          : analysisInput.rentBOR[props.activeUnit].toString(),
        helper:
          property.sqft &&
          property.sqft[props.activeUnit] &&
          analysisInput.rentBOR[props.activeUnit]
            ? currencyFormat2.format(
                analysisInput.rentBOR[props.activeUnit] /
                  property.sqft[props.activeUnit],
              ) + ' rent per sqft'
            : '$0.00 rent per sqft',
        xs: 12,
        lg: 6,
        definition: 'rentBOR',
        // disabled: !isNil(analysisInput.ownerOccupiedBOR) && props.activeUnit === analysisInput.ownerOccupiedBOR,
        tooltipField: 'The monthly house payment',
        dataTour: 'BORDetails-step-4'},
      // {
      //   type: "select",
      //   name: "rehabType",
      //   label: "Rehab Type",
      //   value: assumptionsInput.rehabType,
      //   xs: 12,
      //   options: assumptionsOptions(props.session.me.activeOrg)?.rehabOptions,
      //   assumptions: true
      // },
      // {
      //   type: "dollar",
      //   name: "rehabCost",
      //   label: "Est Rehab",
      //   value: analysisInput.rehabCost,
      //   tooltipButtonText: "Show Estimated Rehab data",
      //   xs: 12,
      //   lg: 6,
      //   definition: "rehabCost",
      //   tooltipField: "An expense that is incurred when restoring an entity to its normal or near-normal condition.",
      //   hide: assumptionsInput.rehabType !== 'singleRehabValue',
      //   dataTour: "rehabExpenses-step-1"
      // },
      // {
      //   type: "budgetLines",
      //   name: "detailedRehabCost",
      //   label: "Rehab Costs",
      //   value: analysisInput.detailedRehabCost,
      //   xs: 12,
      //   options: [
      //     "Appliances",
      //     "Cabinets/Vanities",
      //     "Carpet",
      //     "Countertops/Sinks/Toilets",
      //     "Driveway/Decking/Patio",
      //     "Electrical",
      //     "Exterior",
      //     "Flooring",
      //     "Framing",
      //     "Insulation/Drywall",
      //     "Landscaping",
      //     "Masonry",
      //     "Mechanical",
      //     "Molding/Tile/Trim",
      //     "Paint",
      //     "Plans/Permits",
      //     "Plumbing",
      //     "Roof",
      //     "Siding/Gutters",
      //     "Site Prep",
      //     "Staging",
      //     "Windows/Doors",
      //     "Other"
      //   ],
      //   hide: assumptionsInput.rehabType !== 'detailedRehab',
      //   dataTour: "rehabExpenses-step-1"
      // },
      {
        type: 'select',
        name: 'rehabTierBOR',
        label: 'Rehab Tier',
        value: assumptionsInput.rehabTierBOR,
        tooltipButtonText: 'Show Estimated Rehab data',
        xs: 12,
        lg: 6,
        options: filter(
          activeOrg.rehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        optionName: 'rehabTiers',
        memberOptions: filter(
          activeOrg.memberRehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        dynamicMemberOptions: map(
          filter(
            activeOrg.dynamicMemberRehabTiers,
            (option) =>
              isEmpty(option.showMarkets) ||
              includes(
                option.showMarkets,
                analysisInput.marketType ||
                  props.affiliate?.defaultMarketType,
              ),
          ),
          (option) => {
            let breakPointIndex = 0
            const arv = includes(
              assumptionsInput.strategySpecificFields,
              'afterRepairValue',
            )
              ? analysisInput.afterRepairValueBOR === null
                ? null
                : analysisInput.afterRepairValueBOR
              : analysisInput.afterRepairValue === null
                ? null
                : analysisInput.afterRepairValue
            while (
              option?.breakpoints[breakPointIndex] < arv &&
              breakPointIndex < option?.breakpoints.length
            ) {
              breakPointIndex++
            }
            return {
              ...option,
              label: `$${option.values[breakPointIndex]}/Sqft`}
          },
        ),
        removeOption: true,
        assumptions: true,
        //hide: assumptionsInput.rehabType !== 'rehabTier' && assumptionsInput.rehabType !== 'flippingAmericaRehab',
        dataTour: 'rehabExpenses-step-1',
        helper: currencyFormat.format(
          analytics.BOR.rehabTierValueBOR,
        )},
      {
        name: 'marketType',
        value: !analysisInput.marketType
          ? props.affiliate?.defaultMarketType
          : analysisInput.marketType,
        label: 'Market Type',
        options: concat(
          [props.affiliate?.defaultMarketType],
          props.affiliate?.marketTypes,
        ),
        type: 'select',
        xs: 12,
        lg: 6,
        hide: isEmpty(props.affiliate?.marketTypes)},
      {
        type: 'dollar',
        name: 'rehabDecadeCostBOR',
        label: 'Rehab Per Decade',
        value: assumptionsInput.rehabDecadeCostBOR,
        xs: 12,
        lg: 6,
        assumptions: true,
        dataTour: 'BORDetails-step-6',
        //hide: assumptionsInput.rehabType !== 'flippingAmericaRehab'
      },
      {
        type: 'percentage',
        name: 'rateBOR',
        label: 'Interest Rate',
        value: assumptionsInput.rateBOR || 0,
        xs: 12,
        lg: 6,
        assumptions: true,
        tooltipField:
          'The interest rate is the amount a lender charges for the use of capital, expressed as a percentage of the principal. The interest rate is typically noted on an annual basis known as the annual percentage rate (APR).',
        dataTour: 'financing-step-3'},
      {
        type: 'dollar',
        name: 'downPaymentBOR',
        label: 'Down Payment',
        value: assumptionsInput.downPaymentBOR || 0,
        xs: 12,
        lg: 6,
        assumptions: true,
        tooltipField:
          'The amount of cash that a purchaser must put down, toward the purchase price, to buy real estate.',
        dataTour: 'BORDetails-step-8'},
      {
        type: 'number',
        name: 'termBOR',
        label: 'Term',
        value: assumptionsInput.termBOR || 0,
        adornment: 'Years',
        adornmentToggle: '',
        adornmentPosition: 'end',
        xs: 12,
        lg: 6,
        assumptions: true,
        tooltipField:
          'The length of time that the loan is being amortized. Expressed in years.',
        dataTour: 'financing-step-4'},
    ],
    (field) => !field.hide,
  )

  const BORChartOptions = createDonutChartOptions(
    currencyFormat,
    concat(
      assumptionsInput.rehabBORType === 'detailedRehab'
        ? isEmpty(analysis.detailedRehabCostBOR)
          ? ['Total Rehab']
          : map(
              analysis.detailedRehabCostBOR,
              (line) => line.type || 'Unlabeled',
            )
        : ['Rehab Amount'],
      ['Total Income', 'Downpayment'],
    ),
    pieColors,
  )
  const BORChartSeries = concat(
    assumptionsInput.rehabBORType === 'detailedRehab'
      ? isEmpty(analysis.detailedRehabCostBOR)
        ? [0.0001]
        : map(analysis.detailedRehabCostBOR, (line, index) =>
            index === 0
              ? round(line.value, 2) || 0.0001
              : round(line.value, 2) || 0,
          )
      : [round(analytics.BOR.corTotalBOR, 2) || 0.0001],
    [
      round(analytics?.BOR?.grossIncomeBOR, 2) || 0,
      round(assumptions?.downPaymentBOR, 2) || 0,
    ],
  )

  const strategySummary = {
    ...createStrategySummary(
    analytics,
    'BOR',
    'BOR',
    [
      {
        value: analytics.BOR.averageAnnualReturnBOR || '-',
        arrow: analytics.BOR.averageAnnualReturnBORArrow || 0,
        label: 'Average Annual Return',
        type: 'percent',
        target: assumptions.belowAverageAnnualReturnBOR,
        targetLabel: 'Min Net Profit Target',
        targetName: 'belowAverageAnnualReturnBOR',
        tourStep: 'targets-step-1',
        min: 0,
        max: 30,
        minLabel: '0%',
        maxLabel: '30%',
        description: 'The average annual return',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Total Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.grossIncomeBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -All in cost
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.BOR.allInCostBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Total Net
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.netOperatingIncomeBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  All in cost
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  {currencyFormat.format(
                    round(analytics.BOR.allInCostBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  ROI
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {percentageFormat.format(analytics.BOR.BORNetROI)}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  Term
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'divide']}
                    style={strategyIconGapStyle}
                  />
                  {round(assumptions.termBOR, 0)}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Average Annual Return
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {percentageFormat.format(
                    analytics.BOR.averageAnnualReturnBOR,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </>
        )},
      {
        value: analytics.BOR.IRRBOR || '-',
        arrow: analytics.BOR.IRRBORArrow || 0,
        label: 'IRR',
        target: assumptions.belowIRRBOR,
        targetLabel: 'Min IRR Target',
        targetName: 'belowIRRBOR',
        tourStep: 'targets-step-2',
        min: 0,
        max: 30,
        minLabel: '0%',
        maxLabel: '30%',
        description: `IRR measures an investment's annual return throughout the entire property ownership time frame. Unlike a cash-on-cash return or cap rate that measures returns in annual segments, IRR weighs total earnings from the day you purchase your property to the day you sell it.`,
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategySectionLabelSx}>
                  Due to the complexity of this calculation, we only
                  show an overview of what goes into calculating the
                  IRR
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Initial Investment
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.allInCostBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Gross Annual Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.grossIncomeBOR),
                  )}
                </Typography>
              </Grid>
            </Grid>
            {/* <Grid container alignItems="center" justifyContent="space-between" sx={strategyMetricRowSx}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>Rent Growth Rate</Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>{assumptions.growthRateIncome}%</Typography>
              </Grid>
            </Grid> */}
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Down Payment
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(assumptions.downPaymentBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Holding Period (years)
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.termBOR}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  IRR
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {percentageFormat.format(analytics.BOR.IRRBOR)}
                </Typography>
              </Grid>
            </Grid>
          </>
        )},
    ],
    {
      belowKey: 'belowTargetBOR',
      onKey: 'onTargetBOR',
      aboveKey: 'aboveTargetBOR'},
  ),
    details: [
      {
        value: analytics.BOR.sellPriceBOR || '-',
        label: 'Sale Price',
        type: 'currency',
        tourStep: 'netProfitFromSale-step-1',
        description: 'The Sale Price',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategySectionLabelSx}>
                  Due to the complexity of this calculation, we only
                  show an overview of what goes into calculating the
                  Sale Price
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Gross Annual Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.grossIncomeBOR),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Interest Rate
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.rateBOR}%
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Down Payment
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(assumptions.downPaymentBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Holding Period (years)
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {assumptions.termBOR}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Sale Price
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(analytics.BOR.sellPriceBOR)}
                </Typography>
              </Grid>
            </Grid>
          </>
        )},
      {
        value: analytics.BOR.allInCostBOR || '-',
        label: 'All In Cost',
        type: 'currency',
        tourStep: 'netProfitFromSale-step-2',
        description:
          'Total amount of initial investment into a property, including renovation.',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Purchase Price
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(
                      analysis.offerPrice || analysis.listPrice,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +Closing Costs
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +
                  {currencyFormat.format(
                    round(analytics.BOR.closeCostDollarBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +Custom Acquisition Expenses
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +
                  {currencyFormat.format(
                    round(analytics.customAcquisitionExpenseTotal, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +Rehab Amount
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +
                  {currencyFormat.format(
                    round(analytics.BOR.corTotalBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  All-in Cost
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.allInCostBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </>
        )},
      {
        value: analytics.BOR.grossIncomeBOR || '-',
        label: 'Total Income',
        type: 'currency',
        tourStep: 'netProfitFromSale-step-3',
        description: 'The yearly income',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Annual Rent
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(sum(analysis.rentBOR) * 12, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'xmark']}
                    style={strategyIconGapStyle}
                  />
                  Term
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  <FontAwesomeIcon
                    icon={['fal', 'xmark']}
                    style={strategyIconGapStyle}
                  />
                  {round(assumptions.termBOR, 0)}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Total Rent
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.BOR.grossIncomeBOR -
                        assumptions.downPaymentBOR,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +Down Payment
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  +
                  {currencyFormat.format(
                    round(assumptions.downPaymentBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Total Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.grossIncomeBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </>
        )},
      {
        value: analytics.BOR.netOperatingIncomeBOR || '-',
        label: 'Total Net',
        type: 'currency',
        tourStep: 'netProfitFromSale-step-4',
        description: 'The annual net income',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Total Income
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.grossIncomeBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -All in cost
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analytics.BOR.allInCostBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: "center",
                justifyContent: "space-between"
              }]}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Total Net
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analytics.BOR.netOperatingIncomeBOR, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </>
        )},
    ]}

  const compData = calculateCompData({
    compValues: props.compValues,
    lowestAverage: props.lowestAverage})

  const borIrrExtraContent = (
    <Grid size={12} sx={strategyMarginBottom10Sx}>
                                  <Box sx={{
                                    "& > .MuiPaper-root": {
                                      overflow: "hidden",
                                      boxShadow: "none !important",
                                      borderRadius: 0,
                                      "&:first-of-type": { borderTopLeftRadius: "0.65rem", borderTopRightRadius: "0.65rem" },
                                      "&:last-of-type": { borderBottomLeftRadius: "0.65rem", borderBottomRightRadius: "0.65rem" },
                                      "&:not(:last-of-type)": { borderBottom: 0 },
                                      "& > .MuiPaper-root": { boxShadow: "none !important", borderRadius: "inherit" },
                                    },
                                    "& .panel-title, & .accordion-toggle, & [data-hf=\"panel-title\"], & [data-hf=\"accordion-toggle\"]": { width: "100%", display: "block" },
                                    "& .MuiCard-root > div:has(> .panel-title), & .MuiCard-root > div:has(> [data-hf=\"panel-title\"])": {
                                      display: "block !important",
                                      padding: "0 !important",
                                      width: "100%",
                                      backgroundColor: "transparent !important",
                                    },
                                    "& > .card": {
                                      overflow: "hidden",
                                      "&:not(:last-of-type)": { borderBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                                      "&:not(:first-of-type)": { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
                                      "& > .card-header": { borderRadius: 0, marginBottom: 0 },
                                    },
                                  }}>
                                    <Card variant="outlined" sx={(accordion === 0) && {
                                      "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                                        borderBottomColor: "rgba(79, 80, 98, 0.3)",
                                        background: "#fff",
                                        color: "#070919 !important",
                                        "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
                                      },
                                      "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
                                    }}>
                                      <Card>
                                        <Box sx={strategyTableRowSx}>
                                          <Box data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                                            <Box data-hf="accordion-toggle" sx={{
                                              "& .MuiButton-root": {
                                                display: "flex",
                                                width: "100%",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                textAlign: "left",
                                                fontWeight: 700,
                                                color: "#3b3e66 !important",
                                                textDecoration: "none !important",
                                                textTransform: "none",
                                                borderRadius: 0,
                                                padding: "0.75rem 1.25rem !important",
                                                borderBottom: "1px solid transparent",
                                                background: "#f4f5fd",
                                                "&:hover": {
                                                  background: "#f8f9ff !important",
                                                  color: "#070919 !important",
                                                  textDecoration: "none !important",
                                                  "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                                                },
                                                "&:not(:disabled):hover": { transform: "translateY(0px)" },
                                                "& [data-hf=\"accordion-icon\"]": {
                                                  transform: "rotate(90deg)",
                                                  transition: "all 0.2s ease-in-out",
                                                  marginLeft: "auto",
                                                  color: "#368cff !important",
                                                  overflow: "visible",
                                                },
                                              },
                                              "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                                                background: "#2a2a2a !important",
                                                color: "rgba(255, 255, 255, 0.9) !important",
                                                "&:hover": { background: "#333 !important", color: "#fff !important" },
                                              },
                                            }}>
                                              <Button
                                                variant="text"
                                                size="large"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#3b3e66",
                                                  textDecoration: "none",
                                                  "&:hover": {
                                                    backgroundColor: "transparent",
                                                    color: "#4191ff",
                                                    textDecoration: "underline",
                                                    transform: "translateY(0px)",
                                                  },
                                                  "&:active": { textDecoration: "underline", boxShadow: "none" },
                                                  "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                                                  "& span": {
                                                    position: "relative",
                                                    "&::before": {
                                                      content: "\"\"",
                                                      position: "absolute",
                                                      width: "100%",
                                                      height: 2,
                                                      bottom: 0,
                                                      left: 0,
                                                      visibility: "hidden",
                                                      transform: "scaleX(0)",
                                                      transition: "all 0.3s ease-in-out 0s",
                                                    },
                                                    transition: "all 0.2s ease-in-out",
                                                  },
                                                  "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                                                  "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                                                  "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "space-between",
                                                }}
                                                onClick={() =>
                                                  toggleAccordion(0)
                                                }
                                                aria-expanded={
                                                  accordion === 0
                                                }
                                              >
                                                <span>
                                                  Start Month
                                                </span>
                                                <FontAwesomeIcon icon={[ 'fas', 'angle-up', ]} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                                              </Button>
                                            </Box>
                                          </Box>
                                        </Box>
                                        <Collapse
                                          in={accordion === 0}
                                        >
                                          <Paper sx={strategyFullWidthHiddenSx}>
                                            <TableContainer sx={strategyMaxHeight440Sx}>
                                              <Table
                                                stickyHeader
                                                aria-label="sticky table"
                                              >
                                                <TableHead>
                                                  <TableRow>
                                                    {tableHeadersFirst.map(
                                                      (row) => (
                                                        <TableCell
                                                          align={
                                                            row.align
                                                          }
                                                        >
                                                          {row.name}
                                                        </TableCell>
                                                      ),
                                                    )}
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  <TableRow
                                                    key={
                                                      IRRDataFirst.name
                                                    } sx={strategyTableRowLastSx}>
                                                    <TableCell
                                                      component="th"
                                                      scope="row"
                                                    >
                                                      {
                                                        IRRDataFirst.monthYear
                                                      }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                      {currencyFormat2.format(
                                                        IRRDataFirst.totalOutOfPocket,
                                                      )}
                                                    </TableCell>
                                                  </TableRow>
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Collapse>
                                      </Card>
                                    </Card>
                                    <Card variant="outlined" sx={(accordion === 1) && {
                                      "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                                        borderBottomColor: "rgba(79, 80, 98, 0.3)",
                                        background: "#fff",
                                        color: "#070919 !important",
                                        "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
                                      },
                                      "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
                                    }}>
                                      <Card>
                                        <Box sx={strategyTableRowSx}>
                                          <Box data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                                            <Box data-hf="accordion-toggle" sx={{
                                              "& .MuiButton-root": {
                                                display: "flex",
                                                width: "100%",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                textAlign: "left",
                                                fontWeight: 700,
                                                color: "#3b3e66 !important",
                                                textDecoration: "none !important",
                                                textTransform: "none",
                                                borderRadius: 0,
                                                padding: "0.75rem 1.25rem !important",
                                                borderBottom: "1px solid transparent",
                                                background: "#f4f5fd",
                                                "&:hover": {
                                                  background: "#f8f9ff !important",
                                                  color: "#070919 !important",
                                                  textDecoration: "none !important",
                                                  "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                                                },
                                                "&:not(:disabled):hover": { transform: "translateY(0px)" },
                                                "& [data-hf=\"accordion-icon\"]": {
                                                  transform: "rotate(90deg)",
                                                  transition: "all 0.2s ease-in-out",
                                                  marginLeft: "auto",
                                                  color: "#368cff !important",
                                                  overflow: "visible",
                                                },
                                              },
                                              "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                                                background: "#2a2a2a !important",
                                                color: "rgba(255, 255, 255, 0.9) !important",
                                                "&:hover": { background: "#333 !important", color: "#fff !important" },
                                              },
                                            }}>
                                              <Button
                                                variant="text"
                                                size="large"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#3b3e66",
                                                  textDecoration: "none",
                                                  "&:hover": {
                                                    backgroundColor: "transparent",
                                                    color: "#4191ff",
                                                    textDecoration: "underline",
                                                    transform: "translateY(0px)",
                                                  },
                                                  "&:active": { textDecoration: "underline", boxShadow: "none" },
                                                  "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                                                  "& span": {
                                                    position: "relative",
                                                    "&::before": {
                                                      content: "\"\"",
                                                      position: "absolute",
                                                      width: "100%",
                                                      height: 2,
                                                      bottom: 0,
                                                      left: 0,
                                                      visibility: "hidden",
                                                      transform: "scaleX(0)",
                                                      transition: "all 0.3s ease-in-out 0s",
                                                    },
                                                    transition: "all 0.2s ease-in-out",
                                                  },
                                                  "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                                                  "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                                                  "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "space-between",
                                                }}
                                                onClick={() =>
                                                  toggleAccordion(1)
                                                }
                                                aria-expanded={
                                                  accordion === 1
                                                }
                                              >
                                                <span>
                                                  Appreciation Months
                                                </span>
                                                <FontAwesomeIcon icon={[ 'fas', 'angle-up', ]} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                                              </Button>
                                            </Box>
                                          </Box>
                                        </Box>
                                        <Collapse
                                          in={accordion === 1}
                                        >
                                          <Paper sx={strategyFullWidthHiddenSx}>
                                            <TableContainer sx={strategyMaxHeight440Sx}>
                                              <Table
                                                stickyHeader
                                                aria-label="sticky table"
                                              >
                                                <TableHead>
                                                  <TableRow>
                                                    {tableHeaders.map(
                                                      (row) => (
                                                        <TableCell
                                                          align={
                                                            row.align
                                                          }
                                                        >
                                                          {row.name}
                                                        </TableCell>
                                                      ),
                                                    )}
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {map(
                                                    IRRData,
                                                    (data) => (
                                                      <TableRow
                                                        key={
                                                          data.name
                                                        } sx={{
                                                          '&:last-child td, &:last-child th':
                                                            {
                                                              border: 0}}}>
                                                        <TableCell
                                                          component="th"
                                                          scope="row"
                                                        >
                                                          {
                                                            data.monthYear
                                                          }
                                                        </TableCell>
                                                        <TableCell align="center">
                                                          {currencyFormat2.format(
                                                            data.flow,
                                                          )}
                                                        </TableCell>
                                                      </TableRow>
                                                    ),
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Paper>
                                        </Collapse>
                                      </Card>
                                    </Card>
                                    {/* <Card)}>
                                          <Card>
                                            <div>
                                              <div>
                                                <div>
                                                  <Button
                                                    variant="text"
                                                    size="large"
                                                    onClick={() => toggleAccordion(2)}
                                                    aria-expanded={accordion === 2}
                                                    sx={{
                                                      fontWeight: 700,
                                                      color: "#3b3e66",
                                                      textDecoration: "none",
                                                      "&:hover": {
                                                        backgroundColor: "transparent",
                                                        color: "#4191ff",
                                                        textDecoration: "underline",
                                                        transform: "translateY(0px)",
                                                      },
                                                      "&:active": { textDecoration: "underline", boxShadow: "none" },
                                                      "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                                                      "& span": {
                                                        position: "relative",
                                                        "&::before": {
                                                          content: "\"\"",
                                                          position: "absolute",
                                                          width: "100%",
                                                          height: 2,
                                                          bottom: 0,
                                                          left: 0,
                                                          visibility: "hidden",
                                                          transform: "scaleX(0)",
                                                          transition: "all 0.3s ease-in-out 0s",
                                                        },
                                                        transition: "all 0.2s ease-in-out",
                                                      },
                                                      "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                                                      "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                                                      "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "space-between",
                                                    }}
                                                    <span>Final Month</span>
                                                    <FontAwesomeIcon
                                                      icon={['fas', 'angle-up']}
                                                    />
                                                  </Button>
                                                </Box>
                                              </div>
                                            </div>
                                            <Collapse in={accordion === 2}>
                                              <Paper sx={strategyFullWidthHiddenSx}>
                                                <TableContainer sx={strategyMaxHeight440Sx}>
                                                  <Table stickyHeader aria-label="sticky table">
                                                  <TableHead>
                                                      <TableRow>
                                                        {tableHeaders.map((row) => (
                                                          <TableCell align={row.align}>{row.name}</TableCell>
                                                        ))}
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      <TableRow
                                                        key={last(IRRData).name}
                                                        sx={strategyTableRowLastSx}
                                                      >
                                                        <TableCell component="th" scope="row">{last(IRRData).monthYear}</TableCell>
                                                        <TableCell align="center">{currencyFormat2.format(last(IRRData).flow )}</TableCell>
                                                      </TableRow>
                                                    </TableBody>
                                                  </Table>
                                                </TableContainer>
                                              </Paper>
                                            </Collapse>
                                          </Card>
                                        </Card> */}
                                  </Box>
                                </Grid>
  )
  const irrSection = strategySummary.analytics.find(
    (section) => section.label === 'IRR',
  )
  if (irrSection) irrSection.extraContent = borIrrExtraContent

  const BORCards = [
    {
      name: 'strategy',
      card: (
        <StrategyCard
          strategyName="BOR"
          activeOrg={activeOrg}
          percentage={strategySummary.percentage}
          onStrategyTourClick={toggleStrategyTour}
          shared={props.shared}
          property={property}
          canDrag={props.canDrag}
          contactAgent={
            !props.shared && activeOrg.member !== 'gorillacapital' ? (
              <Box sx={strategyCardBodyPadSx}>
                <ContactAgent
                  property={property}
                  propertyPage={true}
                  canDrag={props.canDrag}
                />
              </Box>
            ) : null
          }
        />
      ),
    },

    {
      name: 'targets',
      card: (
        <TargetsCard
          strategySummary={strategySummary}
          currencyFormat={currencyFormat}
          percentageFormat={percentageFormat}
          modal1={modal1}
          toggle1={toggle1}
          toggle1Close={toggle1Close}
          renderInputFields={renderInputFields}
          assumptionsInput={assumptionsInput}
          showMaxOffer={false}
          onTargetsTourClick={toggleTargetsTour}
        />
      ),
    },

    {
      name: 'BOR',
      card: (
        <Card variant="outlined" sx={{ width: "100%" }}>
          <Box sx={strategyTableRowSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <Box component="small" sx={strategyUppercaseMetaSx}>
                Summary
              </Box>
              <b>BOR Details</b>
            </Box>
            <div>
              <Tooltip title="Breakdown of the resale price minus expenses to show net profit">
                <Button
                  onClick={() => toggleDetailsTour()}
                  sx={{
                    backgroundColor: "rgba(54, 140, 255, 0.15)",
                    color: "#368cff",
                    borderColor: "transparent",
                    "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                    "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                    height: "30px !important",
                    width: "30px !important",
                    lineHeight: "30px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    textAlign: "left",
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div>
          </Box>
          <Divider />
          <List component="div" sx={{
            paddingTop: 0,
            paddingBottom: 0,
            "& .MuiListItem-root, & .MuiListItemButton-root": {
              width: "100%",
              padding: "0.75rem 1.25rem",
              backgroundColor: "#fff",
              border: "1px solid rgba(0, 0, 0, 0.125)",
              borderRadius: 0,
              margin: 0,
              borderWidth: "0 0 1px",
              "&.MuiListItem-button": {
                borderRadius: 0,
                borderWidth: "0 0 1px",
                "& .MuiTouchRipple-root": { opacity: 0.2 },
              },
              "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
              "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
              "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
              "&:first-of-type": { borderTop: "0 !important" },
              "&:last-child": { borderBottom: "0 !important" },
            },
          }}>
            {map(strategySummary.details, (section) => (
              <>
                <ListItemButton
                  data-tour={section.tourStep}
                  disableRipple
                  onClick={() => toggle1(section.label)}
                  sx={{
                    display: "flex",
                    backgroundColor: "#fff",
                    transition: "all 0.2s ease-in-out !important",
                    transform: "scale(1) !important",
                    boxShadow: "none",
                    position: "relative",
                    zIndex: "0",
                    "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                    justifyContent: "space-between",
                    py: "1rem",
                  }}
                >
                  <Box sx={{flex:"1 1 auto"}}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box sx={[{ fontWeight: 700, minWidth: 0 }, {
                      color: "#070919",
                      "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                    }]}>
                      {section.label}
                    </Box>
                    <Box sx={{ fontWeight: 700, textAlign: 'right', flexShrink: 0 }}>
                      {section.value !== '-'
                        ? section.type === 'currency'
                          ? currencyFormat.format(section.value)
                          : section.value > 10
                            ? '+1000%'
                            : percentageFormat.format(
                                section.value,
                              )
                        : section.value}
                    </Box>
                  </Box>
                  </Box>
                </ListItemButton>
                <Dialog
                  open={modal1 === section.label}
                  onClose={toggle1Close}
                  maxWidth="lg"
                  scroll="body"
                  slotProps={{ paper: { sx: calcDialogPaperSx } }}
                >
                  {/* <BlockUi blocking={loader} loader={<PacmanLoader color={'var(--primary)'} loading={true} />}> */}
                  <Grid container spacing={0} direction="row">
                    <Grid size={{sm: 5}}>
                      <Box sx={[strategyFullHeight100Sx, {
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        "& > .divider": { zIndex: 7, width: "100%" },
                        "& .hero-footer": { zIndex: 7 },
                        "& .header-nav-wrapper": { zIndex: 27 },
                        "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                        borderTopLeftRadius: "inherit",
                        borderTopRightRadius: "inherit",
                        alignItems: "center",
                        ".MuiCard-root > &": {
                          top: -1,
                          marginLeft: -1,
                          marginRight: -1,
                          marginBottom: -2,
                          zIndex: 6,
                        },
                        backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
                      }, {
                        "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                      }]}>
                        <Box sx={[strategyFlexCenterRowSx, strategyFlexGrowSx]}>
                          <Box sx={[strategyNavyBgSx, {
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "100%",
                            width: "100%",
                            zIndex: 5,
                            opacity: "0.7 !important",
                          }, {
                            borderRadius: "0.65rem !important",
                            "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                          }]} />
                          <Box sx={[{
                            justifyContent: "center",
                            "@media (min-width: 1440px)": { justifyContent: "flex-start", textAlign: "left" },
                            textAlign: "center",
                            p: "3rem",
                          }, { position: "relative", zIndex: "7", width: "100%" }]}>
                            <Box sx={strategyFooterNoteSx}>
                              <Typography sx={strategyCardTitleSx}>
                                Calculation
                              </Typography>
                            </Box>
                            {section.calculation}
                            <Box sx={strategyFooterNoteSx}>
                              <Typography>
                                * Calculation may be a few dollars off
                                due to rounding
                              </Typography>
                              {section.label === 'Buyer Profit' && (
                                <Typography>
                                  * Resell price includes the
                                  Assignment Fee
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid size={{sm: 7}}>
                      <Box sx={[strategyFullHeight100Sx, {
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        "& > .divider": { zIndex: 7, width: "100%" },
                        "& .hero-footer": { zIndex: 7 },
                        "& .header-nav-wrapper": { zIndex: 27 },
                        "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                      }, {
                        "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                      }]}>
                        <Card sx={[{ width: "100%" }, { flexGrow: 1 }]}>
                          <CardHeader title={section.label} />
                          <Divider />
                          <CardContent>
                            <Grid
                              container
                              direction="row"
                              sx={{
                                alignItems: "center",
                                justifyContent: "center"
                              }}>
                              <Grid size={12} sx={strategyMarginBottom25Sx}>
                                <Card sx={strategyMetricGrayBgSx}>
                                  <CardContent>
                                    <Typography>
                                      {section.description}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>
                          </CardContent>
                          <Divider />
                          <CardActions>
                            <Grid container sx={{
                              justifyContent: "center"
                            }}>
                              <Grid>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={toggle1Close}
                                >
                                  Close
                                </Button>
                              </Grid>
                            </Grid>
                          </CardActions>
                        </Card>
                      </Box>
                    </Grid>
                  </Grid>
                  {/* </BlockUi> */}
                </Dialog>
              </>
            ))}
          </List>
        </Card>
      )},
    {
      name: 'BORInputs',
      card: (
        <Card variant="outlined" sx={{ width: "100%" }}>
          <Grid
            container
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              py: "0.75rem",
              px: "1.25rem",
              mb: 0,
              backgroundColor: "#fff",
              "html[data-color-mode=\"dark\"] &": { backgroundColor: "#1e1e1e !important", color: "rgba(255, 255, 255, 0.87)" },
            }}>
            <Grid size={8}
              sx={{ minWidth: 0 }}>
              <List sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 0.5,
                p: 0,
                m: 0,
                minHeight: 40,
              }}>
                {analysisInput.rentBOR.length > 1 ? (
                  map(analysisInput.rentBOR, (value, index) => (
                    <ListItemButton
                      selected={props.activeUnit === index}
                      onClick={() => props.setActiveUnit(index)}
                      sx={{
                        width: "auto",
                        minWidth: "auto",
                        px: 1.5,
                        py: 1,
                        minHeight: 40,
                        borderRadius: 0,
                        color: "#4f5062",
                        fontWeight: 500,
                        "&.Mui-selected": {
                          color: "#368cff",
                          fontWeight: 700,
                          bgcolor: "transparent",
                          borderBottom: "3px solid #368cff",
                        },
                        "&.Mui-selected:hover": { bgcolor: "transparent", color: "#368cff" },
                      }}
                    >
                      Unit {index + 1}
                    </ListItemButton>
                  ))
                ) : (
                  <ListItem>
                    <Box sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      "& > b": { display: "block" },
                      "& > small": {
                        textTransform: "uppercase",
                        display: "block",
                        opacity: 0.3,
                        fontWeight: "bold",
                      },
                    }}>
                      <b>BOR Inputs</b>
                    </Box>
                  </ListItem>
                )}
              </List>
            </Grid>
            <Grid style={strategyIconGap20Style}>
              <Tooltip title="Estimated expenses allocated to the renovations of a property">
                <Button
                  onClick={() => setOpenBORBORInputsTour(true)}
                  sx={{
                    backgroundColor: "rgba(54, 140, 255, 0.15)",
                    color: "#368cff",
                    borderColor: "transparent",
                    "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                    "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                    height: "30px !important",
                    width: "30px !important",
                    lineHeight: "30px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    textAlign: "left",
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
          <Divider />
          <CardContent>
            <Grid>
              <Grid>
                <Box sx={strategyFlexCenterOnlySx}>
                  <Suspense fallback={<LoadingSkeleton variant="chart" />}>
                    <Chart
                      options={BORChartOptions}
                      series={BORChartSeries}
                      type="donut"
                      width="300"
                    />
                  </Suspense>
                </Box>
              </Grid>
              <Divider sx={strategyBlockSpacerSx} />
              <Grid>
                <Grid
                  container
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center"
                  }}
                >
                  {map(BORFields, (field) =>
                    renderInputFields(field),
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )},
    {
      name: 'comparableProperties',
      card: (
        <ComparablePropertiesCard
          basicView={props.basicView}
          compData={compData}
          onComparablePropertiesTourClick={
            toggleComparablePropertiesTour
          }
          onToggleCenter={props.toggleCenter}
          determinedComps={props.determinedComps}
          compProperties={props.compProperties}
        />
      )},

    {
      name: 'deactive',
      card: <DeactiveCard />},
  ]

  return (
    <>
      <StrategyPageBase
        cards={BORCards}
        strategyName="BOR"
        strategyKey="BOR"
        property={property}
        activeOrg={activeOrg}
        canDrag={props.canDrag}
        publicDashboard={props.publicDashboard}
        publicImageCard={props.publicImageCard}
        session={props.session}
        renderInputFields={renderInputFields}
      />

      {/* TODO COMMENT: Need to alter the tours for the new strategy */}
      <BORStrategyTour
        isOpenBORStrategyTour={openStrategyTour}
        closeBORStrategyTour={closeStrategyTour}
      />
      <BORTargetsTour
        isOpenBORTargetsTour={openTargetsTour}
        closeBORTargetsTour={closeTargetsTour}
      />
      <BORBORDetailsTour
        isOpenBORBORDetailsTour={openDetailsTour}
        closeBORBORDetailsTour={closeDetailsTour}
      />
      <BORBORInputsTour
        isOpenBORBORInputsTour={openBORBORInputsTour}
        closeBORBORInputsTour={() => setOpenBORBORInputsTour(false)}
      />
      <BORComparablePropertiesTour
        isOpenBORComparablePropertiesTour={
          openComparablePropertiesTour
        }
        closeBORComparablePropertiesTour={
          closeComparablePropertiesTour
        }
      />
    </>
  )
}

export default withSession(CenterPageBOR)
